/**
 * Client-side validation for the list-endpoint prefix filters, mirroring the
 * Smithy constraints in LangHubServiceModel on
 * `ListNamespacesRequest.namespaceIdPrefix` (model/namespace/operations.smithy)
 * and `ListStringGroupsRequest.stringGroupIdPrefix`
 * (model/stringgroup/operations.smithy):
 *
 *   @length(min: 0, max: 20)
 *   @pattern("^[a-zA-Z0-9_-]*$")
 *
 * Both identifiers share the same rules. A prefix that violates either would be
 * rejected server-side with a `ValidationException`, so the input is gated here
 * before a request is issued.
 *
 * This is deliberately scoped to values that are SENT TO THE SERVER as a prefix
 * filter. Purely client-side substring filters (for example the string groups
 * table's text filter, which matches free-text fields such as team name) must
 * NOT use it: those fields legitimately contain spaces and other characters
 * that the identifier pattern forbids.
 */

/** Maximum characters accepted by the prefix filters (Smithy `@length` max). */
export const SEARCH_PREFIX_MAX_LENGTH = 20;

/** Characters accepted by the prefix filters (Smithy `@pattern`). */
export const SEARCH_PREFIX_PATTERN = /^[a-zA-Z0-9_-]*$/;

/**
 * Returns a human-readable error for an invalid search prefix, or `null` when
 * the value is acceptable.
 *
 * An empty string is valid: the pattern permits it (`*`, not `+`) and it simply
 * means "no filter". Callers should pass the trimmed value, since that is what
 * is sent to the server / matched against.
 */
export function validateSearchPrefix(value: string): string | null {
  if (value.length > SEARCH_PREFIX_MAX_LENGTH) {
    return `Search term must be at most ${SEARCH_PREFIX_MAX_LENGTH} characters.`;
  }
  if (!SEARCH_PREFIX_PATTERN.test(value)) {
    return 'Use only letters, digits, hyphens, and underscores.';
  }
  return null;
}
